/**
 * Business Context Provider
 *
 * Provides business mode state and organisation data throughout the React component tree.
 * This allows any component to easily check if it's in business mode and access
 * organisation data without prop drilling or repeated GraphQL queries.
 */

import { useQuery } from "@apollo/client";
import type React from "react";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import firebase from "../components/firebase";
import { ORGANISATION } from "../graphql/queries/organisation";
import { type BusinessConfig, detectBusinessContext } from "../utils/businessContext";

/**
 * Organisation data structure from the ORGANISATION query
 */
export interface Organisation {
	_id: string;
	logo?: string;
	squareLogo?: string;
	mapStyle?: string;
	name?: string;
	slug?: string;
	tagline?: string;
	description?: string;
	website?: string;
	email?: string;
	phone?: string;
	whatsapp?: string;
	linkedin?: string;
	facebook?: string;
	instagram?: string;
	tiktok?: string;
	country?: string;
	city?: string;
	zipCode?: string;
	streetAddress?: string;
	companyType?: string;
	businessAccountId?: string;
	embedCta?: "REQUEST_BOOKING" | "SAVE_TRIP";
	theme?: {
		main1?: string;
		main2?: string;
		main3?: string;
		main4?: string;
		brandPrimary?: string;
		brandSecondary?: string;
	};
	milestones?: {
		firstTripCreated?: boolean;
		firstProposalSent?: boolean;
		brandingConfigured?: boolean;
	};
}

export interface BusinessContextValue extends BusinessConfig {
	/**
	 * Force refresh the business context (useful after navigation)
	 */
	refresh: () => void;
	/**
	 * Organisation data from the ORGANISATION query
	 * Available when in business mode and data has loaded
	 */
	organisation?: Organisation;
	/**
	 * Whether the organisation data is currently loading
	 */
	organisationLoading: boolean;
	/**
	 * Refetch the organisation data
	 */
	refetchOrganisation: () => void;
}

/**
 * Exported so tests can inject a value directly rather than mounting `BusinessProvider`,
 * which pulls in Apollo and Firebase. See `src/testUtils/renderWithProviders.tsx`.
 * Application code should use the hooks below, not this.
 */
export const BusinessContext = createContext<BusinessContextValue | undefined>(undefined);

interface BusinessProviderProps {
	children: ReactNode;
}

/**
 * Provider component that wraps the app and provides business context
 * Also fetches and provides organisation data for B2B mode
 */
export const BusinessProvider: React.FC<BusinessProviderProps> = ({ children }) => {
	const [config, setConfig] = useState<BusinessConfig>(() => detectBusinessContext());
	const [user] = useAuthState(firebase.auth);
	const previousUserRef = useRef<typeof user>(undefined);

	// Fetch organisation data — cache-and-network shows cached data immediately while refreshing in background
	const { data: orgData, loading: organisationLoading, refetch } = useQuery(ORGANISATION, {
		fetchPolicy: "cache-and-network",
	});

	const refresh = () => {
		setConfig(detectBusinessContext());
	};

	const refetchOrganisation = async () => {
		await refetch();
	};

	// Refetch organisation data when user logs in
	useEffect(() => {
		// If user just logged in (was null/undefined, now has value), refetch organisation
		if (user && !previousUserRef.current) {
			refetch();
		}
		previousUserRef.current = user;
	}, [user, refetch]);

	// Re-detect on location change
	useEffect(() => {
		const handleLocationChange = () => {
			refresh();
		};

		// Listen for popstate (browser back/forward)
		window.addEventListener("popstate", handleLocationChange);

		// Re-check on mount and when hash/search changes
		handleLocationChange();

		return () => {
			window.removeEventListener("popstate", handleLocationChange);
		};
	}, []);

	const value: BusinessContextValue = {
		...config,
		refresh,
		organisation: orgData?.organisation,
		organisationLoading,
		refetchOrganisation,
	};

	return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

/**
 * Hook to access business context
 *
 * @example
 * ```tsx
 * const { isBusinessMode } = useBusinessContext();
 *
 * if (isBusinessMode) {
 *   return <BusinessFeature />;
 * }
 * ```
 */
export function useBusinessContext(): BusinessContextValue {
	const context = useContext(BusinessContext);

	if (context === undefined) {
		throw new Error("useBusinessContext must be used within a BusinessProvider");
	}

	return context;
}

/**
 * Hook to access organisation data from the business context
 * This is the recommended way to access organisation data - avoids duplicate queries
 *
 * @example
 * ```tsx
 * const { organisation, loading } = useOrganisation();
 *
 * if (loading) return <Spinner />;
 * return <div>{organisation?.name}</div>;
 * ```
 */
export function useOrganisation(): {
	organisation: Organisation | undefined;
	loading: boolean;
	refetch: () => void;
} {
	const context = useContext(BusinessContext);

	if (context === undefined) {
		throw new Error("useOrganisation must be used within a BusinessProvider");
	}

	return {
		organisation: context.organisation,
		loading: context.organisationLoading,
		refetch: context.refetchOrganisation,
	};
}

/**
 * Safe hook to access organisation data from the business context.
 * Unlike useOrganisation, this hook returns undefined when used outside BusinessProvider
 * instead of throwing an error. Useful for components that work in both B2B and regular mode.
 *
 * @example
 * ```tsx
 * const organisation = useOrganisationSafe();
 * // organisation is undefined if not in B2B mode
 * ```
 */
export function useOrganisationSafe(): Organisation | undefined {
	const context = useContext(BusinessContext);
	return context?.organisation;
}

/**
 * Higher-order component that only renders children in business mode
 *
 * @example
 * ```tsx
 * <BusinessOnly>
 *   <BusinessFeature />
 * </BusinessOnly>
 * ```
 */
export const BusinessOnly: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { isBusinessMode } = useBusinessContext();
	return isBusinessMode ? <>{children}</> : null;
};

/**
 * Higher-order component that only renders children when NOT in business mode
 *
 * @example
 * ```tsx
 * <ConsumerOnly>
 *   <ConsumerFeature />
 * </ConsumerOnly>
 * ```
 */
export const ConsumerOnly: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { isBusinessMode } = useBusinessContext();
	return !isBusinessMode ? <>{children}</> : null;
};
